import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from '../librarianSwal';
import { useActiveLibraryId, useSectionBase } from '../../Constant/useActiveLibrary';
import Loader from '../../Layout/Loader';
import SearchableSelect from '../../shared/SearchableSelect';

import {
  getBooksByLibrary,
  getBookCopyById,
  updateBookCopy,
  getErrorMessage
} from '../../api/api';
import '../BookList.css';
import '../lib-page-header.css';

/* Colour coding for the status selects (left-accent border matches value) */
const CONDITION_COLORS = { good: '#16a34a', damaged: '#d97706', lost: '#dc2626' };
const AVAILABILITY_COLORS = {
  available: '#16a34a', reserved: '#7c3aed', issued: '#2563eb',
  damaged: '#d97706', lost: '#dc2626', discarded: '#64748b'
};

/* Small labelled divider that opens each field group */
const GroupDivider = ({ icon, label }) => (
  <div className="col-12">
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      margin: '2px 0 16px', color: '#4f46e5',
      fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em'
    }}>
      <i className={icon} />
      <span>{label}</span>
      <span style={{ flex: 1, height: 1, background: '#e7eef7' }} />
    </div>
  </div>
);

const EditBookCopy = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const sectionBase = useSectionBase();

  const libraryId = useActiveLibraryId();

  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState([]);

  const [formData, setFormData] = useState({
    book_id: '',
    accession_no: '',
    barcode: '',
    catalogue_no: '',
    copy_number: 1,

    room_no: '',
    almirah_no: '',
    rack_no: '',
    shelf_no: '',
    row_no: '',

    purchase_date: '',
    purchase_price: '',
    vendor_name: '',

    condition_status: 'good',
    availability_status: 'available',
    is_reference_only: 0,
    is_discarded: 0,
    discarded_at: '',
    discard_reason: '',
    remarks: ''
  });

  const fetchBooks = async () => {
    try {
      if (!libraryId) return;

      const data = await getBooksByLibrary(libraryId);
      setBooks(data || []);
    } catch (error) {
      console.error('Fetch books error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    }
  };

  const fetchCopyDetails = async () => {
    try {
      if (!id) return;

      setLoading(true);

      const data = await getBookCopyById(id);

      setFormData({
        book_id: data.book_id || '',
        accession_no: data.accession_no || '',
        barcode: data.barcode || '',
        catalogue_no: data.catalogue_no || '',
        copy_number: data.copy_number || 1,

        room_no: data.room_no || '',
        almirah_no: data.almirah_no || '',
        rack_no: data.rack_no || '',
        shelf_no: data.shelf_no || '',
        row_no: data.row_no || '',

        purchase_date: data.purchase_date
          ? String(data.purchase_date).slice(0, 10)
          : '',
        purchase_price: data.purchase_price || '',
        vendor_name: data.vendor_name || '',

        condition_status: data.condition_status || 'good',
        availability_status: data.availability_status || 'available',
        is_reference_only: data.is_reference_only ? 1 : 0,
        is_discarded: data.is_discarded ? 1 : 0,
        discarded_at: data.discarded_at
          ? String(data.discarded_at).slice(0, 10)
          : '',
        discard_reason: data.discard_reason || '',
        remarks: data.remarks || ''
      });
    } catch (error) {
      console.error('Fetch copy details error:', error);
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryId]);

  useEffect(() => {
    fetchCopyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  const validateForm = () => {
    if (!formData.book_id) {
      Swal.fire('Error', 'Please select book', 'error');
      return false;
    }

    if (!formData.accession_no.trim()) {
      Swal.fire('Error', 'Accession no is required', 'error');
      return false;
    }

    if (!formData.barcode.trim()) {
      Swal.fire('Error', 'Barcode is required', 'error');
      return false;
    }

    if (!formData.copy_number) {
      Swal.fire('Error', 'Copy number is required', 'error');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const payload = {
      book_id: formData.book_id,
      accession_no: formData.accession_no.trim(),
      barcode: formData.barcode.trim(),
      catalogue_no: formData.catalogue_no || null,
      copy_number: formData.copy_number,

      room_no: formData.room_no || null,
      almirah_no: formData.almirah_no || null,
      rack_no: formData.rack_no || null,
      shelf_no: formData.shelf_no || null,
      row_no: formData.row_no || null,

      purchase_date: formData.purchase_date || null,
      purchase_price: formData.purchase_price || null,
      vendor_name: formData.vendor_name || null,

      condition_status: formData.condition_status || 'good',
      availability_status: formData.availability_status || 'available',
      is_reference_only: formData.is_reference_only ? 1 : 0,
      is_discarded: formData.is_discarded ? 1 : 0,
      discarded_at: formData.discarded_at || null,
      discard_reason: formData.discard_reason || null,
      remarks: formData.remarks || null
    };

    try {
      setLoading(true);

      await updateBookCopy(id, payload);

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Book copy updated successfully'
      });

      navigate(`${sectionBase}/book-copies?book_id=${formData.book_id}`);
    } catch (error) {
      console.error('Update book copy error:', error);

      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: getErrorMessage(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedBook = books.find(
    (book) => String(book.id) === String(formData.book_id)
  );

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon">
            <i className="fas fa-copy" />
          </div>
          <div>
            <h3 className="lib-page-header__title">Edit Book Copy</h3>
            <div className="lib-page-header__subtitle">Update barcode, accession, location and copy status</div>
          </div>
        </div>
        <div className="lib-page-header__actions">
          <button
            className="btn btn-secondary"
            onClick={() =>
              formData.book_id
                ? navigate(`${sectionBase}/book-copies?book_id=${formData.book_id}`)
                : navigate(`${sectionBase}/book-copies`)
            }
          >
            Back
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card shadow-sm mb-3">
          <div className="card-header fw-bold"><i className="fas fa-book me-2" />Book Selection</div>

          <div className="card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Book *</label>
                  <SearchableSelect
                    name="book_id"
                    value={formData.book_id}
                    onChange={handleChange}
                    options={books.map((book) => ({
                      value: book.id,
                      label: `${book.title} ${book.author_name ? `- ${book.author_name}` : ''}`
                    }))}
                    placeholder="Select Book"
                    required
                    clearable={false}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Selected Book</label>
                  <input
                    type="text"
                    className="form-control"
                    value={
                      selectedBook
                        ? `${selectedBook.title || ''} ${
                            selectedBook.isbn ? `(${selectedBook.isbn})` : ''
                          }`
                        : ''
                    }
                    disabled
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {!loading && (
          <>
            <div className="card shadow-sm mb-3">
              <div className="card-header fw-bold"><i className="fas fa-layer-group me-2" />Copy Details</div>

              <div className="card-body">
                <div className="row">
                  {/* ── Identification ── */}
                  <GroupDivider icon="fas fa-fingerprint" label="Identification" />

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Accession No *</label>
                    <input
                      type="text"
                      name="accession_no"
                      className="form-control"
                      value={formData.accession_no}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Barcode *</label>
                    <input
                      type="text"
                      name="barcode"
                      className="form-control"
                      value={formData.barcode}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Catalogue No</label>
                    <input
                      type="text"
                      name="catalogue_no"
                      className="form-control"
                      value={formData.catalogue_no}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Copy Number *</label>
                    <input
                      type="number"
                      name="copy_number"
                      className="form-control"
                      min="1"
                      value={formData.copy_number}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* ── Location ── */}
                  <GroupDivider icon="fas fa-map-marker-alt" label="Shelf Location" />

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Room No</label>
                    <input
                      type="text"
                      name="room_no"
                      className="form-control"
                      value={formData.room_no}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Almirah No</label>
                    <input
                      type="text"
                      name="almirah_no"
                      className="form-control"
                      value={formData.almirah_no}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Rack No</label>
                    <input
                      type="text"
                      name="rack_no"
                      className="form-control"
                      value={formData.rack_no}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Shelf No</label>
                    <input
                      type="text"
                      name="shelf_no"
                      className="form-control"
                      value={formData.shelf_no}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Row No</label>
                    <input
                      type="text"
                      name="row_no"
                      className="form-control"
                      value={formData.row_no}
                      onChange={handleChange}
                    />
                  </div>

                  {/* ── Purchase ── */}
                  <GroupDivider icon="fas fa-receipt" label="Purchase Info" />

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Purchase Date</label>
                    <input
                      type="date"
                      name="purchase_date"
                      className="form-control"
                      value={formData.purchase_date}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Purchase Price</label>
                    <input
                      type="number"
                      name="purchase_price"
                      className="form-control"
                      value={formData.purchase_price}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Vendor Name</label>
                    <input
                      type="text"
                      name="vendor_name"
                      className="form-control"
                      value={formData.vendor_name}
                      onChange={handleChange}
                    />
                  </div>

                  {/* ── Status & flags ── */}
                  <GroupDivider icon="fas fa-toggle-on" label="Status & Flags" />

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Condition</label>
                    <div style={{ borderLeft: `4px solid ${CONDITION_COLORS[formData.condition_status] || '#94a3b8'}`, borderRadius: '0.375rem' }}>
                      <SearchableSelect
                        name="condition_status"
                        value={formData.condition_status}
                        onChange={handleChange}
                        options={[
                          { value: 'good', label: 'Good' },
                          { value: 'damaged', label: 'Damaged' },
                          { value: 'lost', label: 'Lost' }
                        ]}
                        clearable={false}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label">Availability</label>
                    <div style={{ borderLeft: `4px solid ${AVAILABILITY_COLORS[formData.availability_status] || '#94a3b8'}`, borderRadius: '0.375rem' }}>
                      <SearchableSelect
                        name="availability_status"
                        value={formData.availability_status}
                        onChange={handleChange}
                        options={[
                          { value: 'available', label: 'Available' },
                          { value: 'reserved', label: 'Reserved' },
                          { value: 'issued', label: 'Issued' },
                          { value: 'damaged', label: 'Damaged' },
                          { value: 'lost', label: 'Lost' },
                          { value: 'discarded', label: 'Discarded' }
                        ]}
                        clearable={false}
                      />
                    </div>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label d-block">Reference Only</label>
                    <label
                      className="form-check d-flex align-items-center gap-2 mb-0"
                      htmlFor="isReferenceOnly"
                      style={{
                        minHeight: 42, padding: '0 12px', borderRadius: 10, cursor: 'pointer',
                        border: `1px solid ${formData.is_reference_only ? '#4f46e5' : '#e2e8f0'}`,
                        background: formData.is_reference_only ? '#eef2ff' : '#f8fafc'
                      }}
                    >
                      <input
                        type="checkbox"
                        name="is_reference_only"
                        className="form-check-input mt-0"
                        id="isReferenceOnly"
                        checked={Boolean(formData.is_reference_only)}
                        onChange={handleChange}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, color: formData.is_reference_only ? '#4338ca' : '#64748b' }}>
                        {formData.is_reference_only ? 'Yes — not issuable' : 'No'}
                      </span>
                    </label>
                  </div>

                  <div className="col-md-3 mb-3">
                    <label className="form-label d-block">Discarded</label>
                    <label
                      className="form-check d-flex align-items-center gap-2 mb-0"
                      htmlFor="isDiscarded"
                      style={{
                        minHeight: 42, padding: '0 12px', borderRadius: 10, cursor: 'pointer',
                        border: `1px solid ${formData.is_discarded ? '#dc2626' : '#e2e8f0'}`,
                        background: formData.is_discarded ? '#fef2f2' : '#f8fafc'
                      }}
                    >
                      <input
                        type="checkbox"
                        name="is_discarded"
                        className="form-check-input mt-0"
                        id="isDiscarded"
                        checked={Boolean(formData.is_discarded)}
                        onChange={handleChange}
                      />
                      <span style={{ fontSize: 13, fontWeight: 600, color: formData.is_discarded ? '#b91c1c' : '#64748b' }}>
                        {formData.is_discarded ? 'Yes — removed' : 'No'}
                      </span>
                    </label>
                  </div>

                  {Boolean(formData.is_discarded) && (
                    <>
                      <div className="col-md-3 mb-3">
                        <label className="form-label">Discarded At</label>
                        <input
                          type="date"
                          name="discarded_at"
                          className="form-control"
                          value={formData.discarded_at}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-md-9 mb-3">
                        <label className="form-label">Discard Reason</label>
                        <input
                          type="text"
                          name="discard_reason"
                          className="form-control"
                          value={formData.discard_reason}
                          onChange={handleChange}
                        />
                      </div>
                    </>
                  )}

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Remarks</label>
                    <textarea
                      name="remarks"
                      rows="3"
                      className="form-control"
                      value={formData.remarks}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end align-items-center gap-2 mb-4">
              <button
                type="button"
                className="btn btn-light"
                style={{ minWidth: 120 }}
                onClick={() =>
                  formData.book_id
                    ? navigate(`${sectionBase}/book-copies?book_id=${formData.book_id}`)
                    : navigate(`${sectionBase}/book-copies`)
                }
              >
                <i className="fas fa-times me-1" />Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ minWidth: 150 }}
                disabled={loading}
              >
                {loading
                  ? <><i className="fas fa-spinner fa-spin me-1" />Updating…</>
                  : <><i className="fas fa-save me-1" />Update Copy</>}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default EditBookCopy;